import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useIntervaloVisible } from "./useIntervaloVisible";
import { hashDelBundleEnEjecucion, hashDelBundleEnHtml } from "../versionDelBundle";

// spec: entradas-control#version-nueva

/** Cada cuánto se pregunta mientras la pestaña está visible. */
export const INTERVALO_MS = 15 * 60 * 1000;
/** Nunca más de una petición por minuto, vengan los disparos de donde vengan. */
export const MINIMO_ENTRE_COMPROBACIONES_MS = 60 * 1000;
/** Hash remoto por el que esta pestaña YA recargó una vez (sessionStorage: sobrevive a la recarga). */
export const CLAVE_RECARGA = "clubsagent.version.recargada-por";

// Estado de la PESTAÑA, no de la instancia del hook. Login y Layout lo montan en momentos
// distintos (y Layout se remonta): un remontaje no debe ni repetir la pregunta antes del
// minuto ni olvidar que ya hay versión nueva. Una pestaña, una respuesta.
const pestana = {
  ultimaComprobacion: 0,
  enVuelo: false,
  hashRemoto: null,
  oyentes: new Set(),
};

/** Solo para tests: cada caso arranca con la pestaña «recién abierta». */
export function reiniciarEstadoDeVersion() {
  pestana.ultimaComprobacion = 0;
  pestana.enVuelo = false;
  pestana.hashRemoto = null;
  pestana.oyentes.clear();
}

function leerRecargadaPor() {
  try {
    return window.sessionStorage.getItem(CLAVE_RECARGA);
  } catch {
    return null; // modo privado / almacenamiento bloqueado: como si nunca se hubiera recargado
  }
}

function apuntarRecargadaPor(hash) {
  try {
    window.sessionStorage.setItem(CLAVE_RECARGA, hash);
  } catch {
    // sin almacenamiento no hay guarda; la recarga automática del login sigue siendo una por
    // instancia de página, que es lo que había antes de este hook
  }
}

/**
 * ¿Hay un frontend más nuevo publicado que el que ejecuta esta pestaña?
 *
 * Existe por lo del 07→09-10-2026: la tablet del club lleva días con la app abierta y nadie
 * recarga, así que tras un despliegue sigue ejecutando el bundle viejo. En Sentry se veía
 * como Contabilidad pidiendo el historial ENTERO del club (8-13 s por llamada, bloqueando el
 * servidor para los demás) desde cuentas con el bundle anterior al 05-10, cuatro días
 * después de arreglarlo. `index.html` ya se sirve sin caché y el deploy invalida CloudFront:
 * una recarga real trae lo nuevo. Lo que faltaba era que la pestaña se enterase.
 *
 * Cómo: compara el hash de `main.<hash>.js` en ejecución con el del `index.html` del PROPIO
 * origen. La URL es absoluta (`window.location.origin`) a propósito: `src/index.js` parchea
 * `fetch` y reescribe toda ruta que empiece por `/` al host de la API, así que `/index.html`
 * iría a api.clubsagent.ai. `cache: "no-store"` por si algún intermediario la guardara.
 *
 * Cuándo: al montar, al volver la pestaña a visible y cada 15 min visible
 * (`useIntervaloVisible`), y al cambiar de ruta. El freno de un minuto colapsa los disparos
 * que coinciden (montaje + ruta, Login y luego Layout…). Con la pestaña oculta no pregunta.
 * Sin bundle hasheado (`npm start`) no hay nada que comparar y no se hace ninguna petición.
 *
 * Qué NO hace: recargar por su cuenta. Dentro de la app puede haber un cobro a medias; quien
 * consume el hook decide (la cabecera enseña un botón; el login recarga solo si no hay nada
 * escrito). Para eso devuelve `recargar`, que ANTES de recargar apunta en `sessionStorage` el
 * hash remoto: si tras la recarga la pestaña sigue con el bundle viejo (durante la ventana
 * del despliegue, CloudFront puede servir la ruta `/login` desde su caché de error mientras
 * `/index.html` ya es el nuevo), `recargaAgotada` evita recargar otra vez en bucle por el
 * mismo hash. Un fallo de red o un HTML raro callan: lo peor que pasa es seguir con el
 * bundle viejo, exactamente como hoy. Una vez detectada la versión nueva, deja de preguntar.
 *
 * @param {{ ahora?: () => number }} [opciones]  reloj inyectable (tests).
 * @returns {{ hayVersionNueva: boolean, recargaAgotada: boolean, recargar: () => void }}
 */
export function useVersionNueva({ ahora = Date.now } = {}) {
  const hashLocal = useMemo(() => hashDelBundleEnEjecucion(document), []);
  const [hashRemoto, setHashRemoto] = useState(pestana.hashRemoto);
  const { pathname } = useLocation();

  useEffect(() => {
    pestana.oyentes.add(setHashRemoto);
    setHashRemoto(pestana.hashRemoto); // por si otra instancia respondió entre el render y esto
    return () => {
      pestana.oyentes.delete(setHashRemoto);
    };
  }, []);

  const comprobar = useCallback(async () => {
    // `useIntervaloVisible` ya filtra la pestaña oculta; el disparo por ruta no, y al montar
    // en segundo plano llegaría aquí. La regla es una: oculta, no se pregunta.
    if (!hashLocal || pestana.hashRemoto || pestana.enVuelo || document.hidden) return;
    const t = ahora();
    if (t - pestana.ultimaComprobacion < MINIMO_ENTRE_COMPROBACIONES_MS) return;
    pestana.ultimaComprobacion = t;
    pestana.enVuelo = true;
    try {
      const respuesta = await window.fetch(`${window.location.origin}/index.html`, { cache: "no-store" });
      if (!respuesta.ok) return;
      const remoto = hashDelBundleEnHtml(await respuesta.text());
      if (remoto && remoto !== hashLocal && !pestana.hashRemoto) {
        pestana.hashRemoto = remoto;
        pestana.oyentes.forEach((avisar) => avisar(remoto));
      }
    } catch {
      // Sin red, o un intermediario raro: en silencio. Seguir con el bundle viejo es lo que
      // pasa hoy sin este hook; no hay nada peor que eso.
    } finally {
      pestana.enVuelo = false;
    }
  }, [hashLocal, ahora]);

  useIntervaloVisible(comprobar, INTERVALO_MS);

  useEffect(() => {
    comprobar();
  }, [pathname, comprobar]);

  const recargar = useCallback(() => {
    if (hashRemoto) apuntarRecargadaPor(hashRemoto);
    window.location.reload();
  }, [hashRemoto]);

  const hayVersionNueva = Boolean(hashRemoto);
  const recargaAgotada = hayVersionNueva && leerRecargadaPor() === hashRemoto;

  return { hayVersionNueva, recargaAgotada, recargar };
}
